/**
 * AR Vision Demo — chat-input interceptor.
 *
 * THROWAWAY DEMO CODE. `useArDemoInterceptor()` returns a `tryIntercept`
 * function the global chat renderer calls at the top of its send handler. When
 * demo mode is on AND the user's message matches a scripted beat, it:
 *   1. streams the beat's narration into the chat (via the agent-state
 *      demo escape hatch — no backend call), and
 *   2. fires the beat's registered frontend tools in order.
 * It returns `true` to signal the renderer to STOP (skip the real RPC send).
 * When demo mode is off or no beat matches, it returns `false` and the renderer
 * proceeds normally.
 */

import { useCallback } from 'react';
import { useAgentState } from '@/context/agent-state.context';
import { isArDemoMode } from './demo-mode';
import { getArDemoTool, matchArDemoScript } from './demo-script';

export function useArDemoInterceptor(): {
  tryIntercept: (message: string) => boolean;
} {
  const { runDemoScriptedTurn } = useAgentState();

  const tryIntercept = useCallback(
    (message: string): boolean => {
      if (!isArDemoMode()) return false;
      const script = matchArDemoScript(message);
      if (!script) return false;

      const runTools = async () => {
        for (const toolName of script.toolNames) {
          const tool = getArDemoTool(toolName);
          if (!tool) {
            console.warn(
              `[ar-demo] scripted tool "${toolName}" not registered yet (beat may be unbuilt)`,
            );
            continue;
          }
          try {
            await tool.execute(undefined as never);
          } catch (err) {
            console.error(`[ar-demo] tool "${toolName}" threw`, err);
          }
        }
      };

      // Most beats narrate first, then act. Beats flagged `narrateAfterTools`
      // (e.g. building the workflow) run their tools first so the narration
      // only lands once the on-screen action finishes.
      if (script.narrateAfterTools) {
        void runDemoScriptedTurn(
          message,
          script.narration,
          undefined,
          runTools,
        );
      } else {
        void runDemoScriptedTurn(message, script.narration, runTools);
      }

      return true;
    },
    [runDemoScriptedTurn],
  );

  return { tryIntercept };
}
